import React, { useState, useEffect, useRef } from 'react';

/* ====================================================================
   SCORE ENTRY MODAL — keyboard-navigable, fast entry
   ==================================================================== */
function ScoreModal({ match, comp, onClose, onSubmit, isAdmin, canSubmit = true }) {
  const matchType = comp?.matchType || 'ends';
  const isSetsMode = matchType === 'sets';
  const isFinalMatch = match?.bracketType === 'finals';
  const totalFinalsRounds = comp?.finalsFormat === 'quarter-final' ? 3 : comp?.finalsFormat === 'semi-final' ? 2 : 1;
  const currentFinalsRound = isFinalMatch ? (match?.bracketRound || totalFinalsRounds) : 0;
  const finalsStage = !isFinalMatch
    ? null
    : currentFinalsRound === totalFinalsRounds
      ? 'final'
      : currentFinalsRound === (totalFinalsRounds - 1)
        ? 'semi'
        : 'quarter';
  const stageEnds = finalsStage === 'quarter'
    ? comp?.quarterFinalEnds
    : finalsStage === 'semi'
      ? comp?.semiFinalEnds
      : finalsStage === 'final'
        ? comp?.finalEnds
        : null;
  const fixedEnds = matchType === 'ends'
    ? (isFinalMatch
      ? (stageEnds || comp?.finalsEnds || comp?.numberOfEnds || 18)
      : (comp?.numberOfEnds || 18))
    : null;
  const setsCount = isSetsMode ? Math.max(1, parseInt(comp?.numberOfSets || 3, 10)) : 0;
  const endsPerSet = isSetsMode ? Math.max(1, parseInt(comp?.endsPerSet || 9, 10)) : 0;
  const baseSetEndCount = isSetsMode ? (setsCount * endsPerSet) : 0;
  const defaultEnds = isSetsMode ? baseSetEndCount : (fixedEnds || 25);
  const extraEndOnDraw = comp?.extraEndOnDraw || false;

  // endScores may arrive as a JSON string from PostgreSQL — ensure it's an array
  const parseEndScores = (raw) => {
    if (!raw) return null;
    if (Array.isArray(raw)) return raw.length > 0 ? raw : null;
    if (typeof raw === 'string') { try { const p = JSON.parse(raw); return Array.isArray(p) && p.length > 0 ? p : null; } catch (_) { return null; } }
    return null;
  };

  const [endScores, setEndScores] = useState(() => {
    const parsed = parseEndScores(match.endScores);
    if (parsed) {
      // For fixed ends, pad or trim to match the expected count
      if (fixedEnds && parsed.length < fixedEnds) {
        return [...parsed, ...Array.from({ length: fixedEnds - parsed.length }, () => ({ home: null, away: null }))];
      }
      return parsed;
    }
    return Array.from({ length: defaultEnds }, () => ({ home: null, away: null }));
  });
  const [homeScore, setHomeScore] = useState(match.homeScore || 0);
  const [awayScore, setAwayScore] = useState(match.awayScore || 0);
  const inputRefs = useRef([]);

  const homeName = match.HomeTeam ? match.HomeTeam.name : 'Home';
  const awayName = match.AwayTeam ? match.AwayTeam.name : 'Away';

  // Recalculate totals
  useEffect(() => {
    if (matchType === 'ends' || matchType === 'first-to' || matchType === 'timed' || isSetsMode) {
      let h = 0, a = 0;
      endScores.forEach(e => { h += e.home || 0; a += e.away || 0; });
      setHomeScore(h);
      setAwayScore(a);
    }
  }, [endScores, matchType, isSetsMode]);

  const updateEnd = (idx, side, val) => {
    if (!canSubmit) return;
    const updated = [...endScores];
    const score = val === '' ? null : (parseInt(val, 10) || 0);
    if (side === 'home') {
      updated[idx] = { home: score, away: (score || 0) > 0 ? 0 : updated[idx].away };
    } else {
      updated[idx] = { home: (score || 0) > 0 ? 0 : updated[idx].home, away: score };
    }
    setEndScores(updated);
  };

  // Tab/Enter moves down the column first (all home ends, then all away ends)
  // When in the away column, skip ends where home already scored (only one side scores per end)
  const handleKeyNav = (e, idx, side) => {
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      let nextIdx = idx + 1, nextSide = side;
      if (nextIdx >= endScores.length) {
        nextIdx = 0; nextSide = side === 'home' ? 'away' : 'home';
      }
      // In the away column, skip rows where home already has a score
      if (nextSide === 'away') {
        let start = nextIdx, looped = false;
        while (endScores[nextIdx] && endScores[nextIdx].home > 0) {
          nextIdx++;
          if (nextIdx >= endScores.length) { nextIdx = 0; looped = true; }
          if (nextIdx === start) break; // all rows have home scores, stop
        }
      }
      const refKey = `${nextIdx}-${nextSide}`;
      if (inputRefs.current[refKey]) {
        inputRefs.current[refKey].focus();
        inputRefs.current[refKey].select();
      }
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      let prevIdx = idx - 1, prevSide = side;
      if (prevIdx < 0) {
        prevIdx = endScores.length - 1; prevSide = side === 'away' ? 'home' : 'away';
      }
      // In the away column, skip rows where home already has a score (going backwards)
      if (prevSide === 'away') {
        let start = prevIdx;
        while (endScores[prevIdx] && endScores[prevIdx].home > 0) {
          prevIdx--;
          if (prevIdx < 0) { prevIdx = endScores.length - 1; }
          if (prevIdx === start) break;
        }
      }
      const refKey = `${prevIdx}-${prevSide}`;
      if (inputRefs.current[refKey]) {
        inputRefs.current[refKey].focus();
        inputRefs.current[refKey].select();
      }
    }
  };

  const addEnd = () => {
    if (!canSubmit) return;
    setEndScores(prev => [...prev, { home: null, away: null }]);
  };

  const setSummaries = isSetsMode
    ? Array.from({ length: setsCount }, (_, setIdx) => {
      const start = setIdx * endsPerSet;
      const slice = endScores.slice(start, start + endsPerSet);
      let homeShots = 0;
      let awayShots = 0;
      slice.forEach(e => {
        homeShots += e?.home || 0;
        awayShots += e?.away || 0;
      });
      return { setNumber: setIdx + 1, homeShots, awayShots };
    })
    : [];

  const homeSetsWon = setSummaries.reduce((acc, s) => acc + (s.homeShots > s.awayShots ? 1 : 0), 0);
  const awaySetsWon = setSummaries.reduce((acc, s) => acc + (s.awayShots > s.homeShots ? 1 : 0), 0);
  const setsTied = isSetsMode && homeSetsWon === awaySetsWon && homeSetsWon > 0;

  // Extra end on draw: when enabled and scores are tied, add one extra end row
  const isDraw = homeScore === awayScore && homeScore > 0;
  const showExtraEnd = isSetsMode
    ? (extraEndOnDraw && comp?.scoringMethod === 'twilight' && setsTied && endScores.length === baseSetEndCount)
    : (extraEndOnDraw && isDraw && fixedEnds && endScores.length === fixedEnds);
  const displayEndScores = showExtraEnd
    ? [...endScores, { home: null, away: null }]
    : endScores;

  // Sync displayEndScores back when user types into the extra end
  const updateDisplayEnd = (idx, side, val) => {
    if (!canSubmit) return;
    if (idx >= endScores.length) {
      // User is typing in the extra end — expand endScores
      const expanded = [...endScores, { home: null, away: null }];
      const score = val === '' ? null : (parseInt(val, 10) || 0);
      if (side === 'home') {
        expanded[idx] = { home: score, away: (score || 0) > 0 ? 0 : expanded[idx].away };
      } else {
        expanded[idx] = { home: (score || 0) > 0 ? 0 : expanded[idx].home, away: score };
      }
      setEndScores(expanded);
    } else {
      updateEnd(idx, side, val);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (matchType === 'ends' || matchType === 'first-to' || matchType === 'timed') {
      // Keep end ordering intact: do not drop blank ends in the middle,
      // otherwise scores shift up to the wrong end number.
      if (fixedEnds) {
        onSubmit(match.id, { endScores });
      } else {
        // For variable-length modes, trim only trailing blank ends.
        let lastScored = -1;
        for (let i = endScores.length - 1; i >= 0; i--) {
          const e = endScores[i] || {};
          if ((e.home || 0) > 0 || (e.away || 0) > 0) { lastScored = i; break; }
        }
        const trimmed = lastScored >= 0 ? endScores.slice(0, lastScored + 1) : endScores;
        onSubmit(match.id, { endScores: trimmed });
      }
    } else if (isSetsMode) {
      const setScores = Array.from({ length: setsCount }, (_, setIdx) => {
        const start = setIdx * endsPerSet;
        const slice = endScores.slice(start, start + endsPerSet);
        let homeShots = 0;
        let awayShots = 0;
        slice.forEach(e => {
          homeShots += e?.home || 0;
          awayShots += e?.away || 0;
        });
        return { setNumber: setIdx + 1, homeShots, awayShots };
      });
      onSubmit(match.id, { endScores, setScores });
    } else {
      onSubmit(match.id, { homeScore, awayScore });
    }
  };

  const useTwoColumns = displayEndScores.length > 6;
  const half = Math.ceil(displayEndScores.length / 2);

  const total = displayEndScores.length;

  const baseEndsForExtra = isSetsMode ? baseSetEndCount : fixedEnds;
  const isExtraEnd = (idx) => baseEndsForExtra && idx >= baseEndsForExtra;

  const renderEndRow = (e, idx) => (
    <tr key={idx} style={isExtraEnd(idx) ? { background: '#FFF9C4' } : undefined}>
      <td className="w3-center" style={{ padding: '1px 2px' }}>
        <strong>{isExtraEnd(idx) ? 'EE' : idx + 1}</strong>
      </td>
      <td style={{ padding: '1px 2px' }}>
        <input className="w3-input w3-border w3-round score-input" type="number" min="0"
          tabIndex={idx + 1}
          ref={el => inputRefs.current[`${idx}-home`] = el}
          value={e.home ?? ''} onChange={ev => updateDisplayEnd(idx, 'home', ev.target.value)}
          readOnly={!canSubmit}
          onKeyDown={ev => handleKeyNav(ev, idx, 'home')}
          onFocus={ev => ev.target.select()} />
      </td>
      <td style={{ padding: '1px 2px' }}>
        <input className="w3-input w3-border w3-round score-input" type="number" min="0"
          tabIndex={total + idx + 1}
          ref={el => inputRefs.current[`${idx}-away`] = el}
          value={e.away ?? ''} onChange={ev => updateDisplayEnd(idx, 'away', ev.target.value)}
          readOnly={!canSubmit}
          onKeyDown={ev => handleKeyNav(ev, idx, 'away')}
          onFocus={ev => ev.target.select()} />
      </td>
    </tr>
  );

  return (
    <div className="w3-modal" style={{ display: 'block', alignItems: 'flex-start', paddingTop: '10px' }} onClick={onClose}>
      <div className="w3-modal-content w3-card-4 w3-animate-zoom w3-round score-modal" style={{ maxWidth: useTwoColumns ? '700px' : '500px' }}
        onClick={e => e.stopPropagation()}>
        <header className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div>
              <h4 style={{ margin: 0 }}>Enter Scores</h4>
              <p className="w3-small" style={{ margin: '2px 0 0', opacity: 0.9 }}>
                <strong>{homeName}</strong> vs <strong>{awayName}</strong> &bull; R{match.round} &bull; Rink {match.rink}
              </p>
            </div>
            <span className="w3-button w3-hover-red w3-round" onClick={onClose} style={{ padding: '2px 10px', marginLeft: '8px' }}>&times;</span>
          </div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
            {canSubmit ? (
              <button className="w3-button w3-round w3-green" onClick={handleSubmit} style={{ padding: '8px 20px', fontWeight: 700, fontSize: '1em', boxShadow: '0 2px 8px rgba(76,175,80,0.4)' }}>
                <i className="fa fa-check w3-margin-right"></i>{isAdmin ? 'Save Scores' : 'Submit for Approval'}
              </button>
            ) : (
              <span className="w3-tag w3-round w3-light-grey"><i className="fa fa-eye w3-margin-right"></i>View only</span>
            )}
            {canSubmit && (matchType === 'timed' || matchType === 'first-to') && (
              <button className="w3-button w3-tiny w3-round w3-theme-l3" onClick={addEnd} style={{ padding: '4px 10px' }}>
                <i className="fa fa-plus"></i> Add End
              </button>
            )}
            {isAdmin && canSubmit && (
            <button className="w3-button w3-tiny w3-round w3-orange" onClick={() => onSubmit(match.id, { notPlayed: true })} style={{ padding: '4px 8px' }}>
              <i className="fa fa-ban"></i> Not Played
            </button>
            )}
            {isAdmin && canSubmit && (
            <button className="w3-button w3-tiny w3-round w3-deep-orange" onClick={() => onSubmit(match.id, { forfeit: 'win-away' })} style={{ padding: '4px 8px' }}>
              <i className="fa fa-flag"></i> {homeName} forfeit
            </button>
            )}
            {isAdmin && canSubmit && (
            <button className="w3-button w3-tiny w3-round w3-deep-orange" onClick={() => onSubmit(match.id, { forfeit: 'win-home' })} style={{ padding: '4px 8px' }}>
              <i className="fa fa-flag"></i> {awayName} forfeit
            </button>
            )}
          </div>
        </header>

        <div className="w3-container" style={{ padding: '8px 16px' }}>
          {(matchType === 'ends' || matchType === 'first-to' || matchType === 'timed') ? (
            <>
              {/* Running totals — centered above each table half */}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', margin: '8px 0 12px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.15em' }}>{homeName}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="w3-text-theme" style={{ fontWeight: 800, fontSize: '2.2em', lineHeight: 1 }}>{homeScore}</span>
                  <span className="w3-text-grey" style={{ fontSize: '1.2em', fontWeight: 300 }}>-</span>
                  <span className="w3-text-theme" style={{ fontWeight: 800, fontSize: '2.2em', lineHeight: 1 }}>{awayScore}</span>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.15em' }}>{awayName}</div>
                </div>
              </div>

              {useTwoColumns ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <table className="w3-table w3-bordered w3-tiny score-table-compact score-table-2col" style={{ flex: 1 }}>
                    <thead className="w3-theme-l3">
                      <tr>
                        <th className="w3-center" style={{ width: '28px' }}>End</th>
                        <th className="w3-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>{homeName}</th>
                        <th className="w3-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>{awayName}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEndScores.slice(0, half).map((e, i) => renderEndRow(e, i))}
                    </tbody>
                  </table>
                  <table className="w3-table w3-bordered w3-tiny score-table-compact score-table-2col" style={{ flex: 1 }}>
                    <thead className="w3-theme-l3">
                      <tr>
                        <th className="w3-center" style={{ width: '28px' }}>End</th>
                        <th className="w3-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>{homeName}</th>
                        <th className="w3-center" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 }}>{awayName}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEndScores.slice(half).map((e, i) => renderEndRow(e, half + i))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="w3-responsive">
                  <table className="w3-table w3-bordered w3-small score-table-compact">
                    <thead className="w3-theme-l3">
                      <tr><th className="w3-center" style={{ width: '50px' }}>End</th><th className="w3-center">{homeName}</th><th className="w3-center">{awayName}</th></tr>
                    </thead>
                    <tbody>
                      {displayEndScores.map((e, i) => renderEndRow(e, i))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : isSetsMode ? (
            <>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', margin: '8px 0 12px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.05em' }}>{homeName}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="w3-tag w3-round w3-theme-l4" style={{ fontWeight: 700 }}>Sets {homeSetsWon} - {awaySetsWon}</span>
                  <span className="w3-tag w3-round w3-pale-blue" style={{ fontWeight: 700 }}>Shots {homeScore} - {awayScore}</span>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.05em' }}>{awayName}</div>
                </div>
              </div>

              <div className="w3-row-padding" style={{ marginTop: '4px' }}>
                {Array.from({ length: setsCount }, (_, setIdx) => {
                  const start = setIdx * endsPerSet;
                  const setRows = displayEndScores.slice(start, start + endsPerSet);
                  const summary = setSummaries[setIdx] || { homeShots: 0, awayShots: 0 };
                  const winner = summary.homeShots > summary.awayShots ? homeName : (summary.awayShots > summary.homeShots ? awayName : null);
                  return (
                    <div key={setIdx} className="w3-col m6 s12" style={{ marginBottom: '8px' }}>
                      <div className="w3-card w3-round w3-border" style={{ overflow: 'hidden' }}>
                        <div className="w3-container w3-theme-l4" style={{ padding: '4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong>Set {setIdx + 1}</strong>
                          <span className="w3-small">{summary.homeShots} - {summary.awayShots}{winner ? ` (${winner})` : ''}</span>
                        </div>
                        <table className="w3-table w3-bordered w3-small score-table-compact" style={{ marginBottom: 0 }}>
                          <thead className="w3-theme-l3">
                            <tr>
                              <th className="w3-center" style={{ width: '42px' }}>End</th>
                              <th className="w3-center">{homeName}</th>
                              <th className="w3-center">{awayName}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {setRows.map((e, localIdx) => renderEndRow(e, start + localIdx))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>

              {(extraEndOnDraw && comp?.scoringMethod === 'twilight' && setsTied) && (
                <div className="w3-card w3-round w3-border" style={{ marginTop: '8px', overflow: 'hidden' }}>
                  <div className="w3-container w3-amber" style={{ padding: '6px 10px' }}>
                    <strong><i className="fa fa-flag-checkered w3-margin-right"></i>Tie-Breaker Extra End</strong>
                    <span className="w3-small" style={{ marginLeft: '8px' }}>Sets are tied, so this end decides the match winner.</span>
                  </div>
                  <table className="w3-table w3-bordered w3-small score-table-compact" style={{ marginBottom: 0 }}>
                    <thead className="w3-theme-l3">
                      <tr><th className="w3-center" style={{ width: '58px' }}>End</th><th className="w3-center">{homeName}</th><th className="w3-center">{awayName}</th></tr>
                    </thead>
                    <tbody>
                      {[renderEndRow(displayEndScores[baseSetEndCount] || { home: null, away: null }, baseSetEndCount)]}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div className="w3-row-padding" style={{ marginTop: '12px' }}>
              <div className="w3-half w3-center">
                <label className="w3-text-grey">{homeName}</label>
                <input className="w3-input w3-border w3-round w3-center w3-xlarge" type="number" min="0" value={homeScore}
                  onChange={e => canSubmit && setHomeScore(parseInt(e.target.value) || 0)}
                  readOnly={!canSubmit} />
              </div>
              <div className="w3-half w3-center">
                <label className="w3-text-grey">{awayName}</label>
                <input className="w3-input w3-border w3-round w3-center w3-xlarge" type="number" min="0" value={awayScore}
                  onChange={e => canSubmit && setAwayScore(parseInt(e.target.value) || 0)}
                  readOnly={!canSubmit} />
              </div>
            </div>
          )}
        </div>


      </div>
    </div>
  );
}

export default ScoreModal;
